import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BordLateralModal,
  BordSelect,
  BordTextArea,
  BordUploaderButton,
  FORM_COMPONENTS_STANDARD_SIZES
} from 'bord-design-system';
import type { IDocumentDetail, TBordSelectOption } from 'bord-design-system';
import { BordOneToneIcon } from 'components/NudosComponents';
import { updateBulkToolState } from 'services/toolsModule';
import ToolsBulkSelectedToolsCollapse from '../ToolsBulkSelectedToolsCollapse/ToolsBulkSelectedToolsCollapse';
import { IToolsBulkSelectionItem } from 'types/products';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { buildBulkToolStatusFormData, buildBulkToolStatusSelectOptions } from '../../productsModuleListing.utils';
import { bordColorTheme } from 'styles/styles.constants';

import './ToolsBulkStatusChangeModal.scss';

interface IToolsBulkStatusChangeModalProps {
  open: boolean;
  onClose: () => void;
  selectedTools: IToolsBulkSelectionItem[];
  selectedStatusId: string;
  statusChangeReason: string;
  statusEvidenceFile?: File;
  onUnselectTool: (toolId: number) => void;
  onSelectedStatusIdChange: (selectedStatusId: string) => void;
  onStatusChangeReasonChange: (statusChangeReason: string) => void;
  onStatusEvidenceFileChange: (statusEvidenceFile?: File) => void;
  onBulkStatusUpdated: () => void | Promise<void>;
}

/**
 * Muestra el formulario para aplicar un cambio masivo de estado a las herramientas seleccionadas.
 */
const ToolsBulkStatusChangeModal = ({
  open,
  onClose,
  selectedTools,
  selectedStatusId,
  statusChangeReason,
  statusEvidenceFile,
  onUnselectTool,
  onSelectedStatusIdChange,
  onStatusChangeReasonChange,
  onStatusEvidenceFileChange,
  onBulkStatusUpdated
}: IToolsBulkStatusChangeModalProps) => {
  const [isSubmittingBulkStatus, setIsSubmittingBulkStatus] = useState(false);
  const isMountedRef = useRef(true);

  const statusSelectOptions: ReadonlyArray<TBordSelectOption> = useMemo(() => {
    return buildBulkToolStatusSelectOptions();
  }, []);

  const statusEvidenceFileUrl = useMemo(() => {
    if (!statusEvidenceFile) return undefined;

    return URL.createObjectURL(statusEvidenceFile);
  }, [statusEvidenceFile]);

  const defaultStatusEvidenceFileDetail = useMemo<IDocumentDetail>(
    () => ({
      documentName: statusEvidenceFile?.name ?? '',
      documentFile: statusEvidenceFile,
      url: statusEvidenceFileUrl
    }),
    [statusEvidenceFile, statusEvidenceFileUrl]
  );

  const isSubmitButtonDisabled = isSubmittingBulkStatus || !selectedStatusId?.trim() || !statusChangeReason?.trim();

  const modalLeadingIcon = (
    <div className="toolsBulkStatusChangeModalLeading">
      <BordOneToneIcon variant="refreshCw" standardSize={24} stroke={bordColorTheme.navyBlue[900]} />
    </div>
  );

  const updateSubmittingBulkStatus = (nextValue: boolean): void => {
    if (!isMountedRef.current) {
      return;
    }

    setIsSubmittingBulkStatus(nextValue);
  };

  const handleSubmitBulkStatus = async () => {
    if (!selectedStatusId?.trim() || !statusChangeReason?.trim() || selectedTools.length === 0) {
      return;
    }

    updateSubmittingBulkStatus(true);

    try {
      const formData = buildBulkToolStatusFormData({
        productIds: selectedTools.map(tool => tool.id),
        selectedStatusId,
        statusChangeReason,
        statusEvidenceFile
      });
      await updateBulkToolState(formData);
      displaySuccessNotification({
        customJSXMessage: <>El estado de las herramientas se ha modificado exitosamente</>
      });
      await onBulkStatusUpdated();
    } catch (error) {
      displayErrorNotification();
    } finally {
      if (isMountedRef.current) {
        updateSubmittingBulkStatus(false);
        onClose();
      }
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (statusEvidenceFileUrl) {
        URL.revokeObjectURL(statusEvidenceFileUrl);
      }
    };
  }, [statusEvidenceFileUrl]);

  const modalContent = (
    <div className="toolsBulkStatusChangeModalBody" aria-label={`Herramientas seleccionadas: ${selectedTools.length}`}>
      <ToolsBulkSelectedToolsCollapse tools={selectedTools} onUnselectTool={onUnselectTool} width="46.4rem" />

      <div className="toolsBulkStatusChangeModalField">
        <BordSelect
          label="Estado"
          placeholder="Selecciona un estado"
          triggerVariant="default"
          indicatorPlacement="start"
          dropdownMaxHeight="17rem"
          dropdownSide="bottom"
          triggerWidth="46.4rem"
          dropdownWidth="46.4rem"
          value={selectedStatusId}
          options={statusSelectOptions}
          onChange={onSelectedStatusIdChange}
        />
      </div>

      <div className="toolsBulkStatusChangeModalField">
        <BordTextArea
          label="Razón de la modificación"
          placeholder="Escribe aquí las razones..."
          value={statusChangeReason}
          setInputValue={onStatusChangeReasonChange}
          textareaHeight="7.2rem"
          textareaWidth="46.4rem"
        />
      </div>

      <div className="toolsBulkStatusChangeModalField toolsBulkStatusChangeModalUploader">
        <BordUploaderButton
          label="Subir archivo PDF (Opcional)"
          defaultFile={defaultStatusEvidenceFileDetail}
          isFilled={!!statusEvidenceFile}
          handleChangeFile={selectedFile => onStatusEvidenceFileChange(selectedFile)}
          handleDeleteFile={() => onStatusEvidenceFileChange(undefined)}
          handleDownloadFile={url => {
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          emptyBordUploaderButtonTitle="el archivo de la evidencia"
          emptyBordUploaderButtonSubtitle="Formatos aceptados: PDF. Peso máximo: 5MB"
          acceptFileExtension={['.pdf']}
          customWidth={FORM_COMPONENTS_STANDARD_SIZES.W464}
        />
      </div>
    </div>
  );

  return (
    <BordLateralModal
      open={open}
      onClose={onClose}
      bordLateralModalHeaderProps={{
        title: 'Cambiar estado masivamente',
        subtitle: 'El estado aplicará a todas las herramientas seleccionadas',
        leading: modalLeadingIcon,
        onClose
      }}
      bordLateralModalContent={modalContent}
      panelClassName="toolsBulkStatusChangeModalPanel"
      bordLateralModalFooterClassName="bordLateralModalFooterDScenteredFullWidth"
      bordLateralModalFooterProps={{
        firstButton: {
          modeButton: 'tertiary',
          bordButtonStyle: 'outlined',
          label: 'Volver',
          disabled: isSubmittingBulkStatus,
          onClick: onClose
        },
        secondButton: {
          modeButton: 'primary',
          label: 'Cambiar estado',
          disabled: isSubmitButtonDisabled,
          onClick: handleSubmitBulkStatus
        }
      }}
    />
  );
};

export default ToolsBulkStatusChangeModal;
